import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Link, Paper, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChequeForm from "./ChequeForm";
import usePaginado from "./usePaginado";
import { SUB_COLORES } from "./coloresSub";
import { PiePaginacion } from "./ListadoTabla";
import TraspasoDineroForm from "./TraspasoDineroForm";
import ReciboCreditoForm from "./ReciboCreditoForm";
import ComprobanteDialog from "./ComprobanteDialog";
import ConceptosDialog from "./ConceptosDialog";
import NombresDialog from "./NombresDialog";
import { fmtFecha, fmtMoney, getCatalogosEgresos, listarCheques } from "../services/egresosService";

const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };

// Pestaña "Egreso de Cheques": formulario del cheque / traspaso de dinero / recibo de
// crédito arriba y, debajo, los últimos cheques emitidos (espejo de co_egreso_pro/busqueda.php).
export default function EgresoChequesTab({ onIrABusqueda }) {
  const [catalogos, setCatalogos] = useState(null);
  const [errorCatalogos, setErrorCatalogos] = useState("");
  const [sub, setSub] = useState(0);

  const [buscar, setBuscar] = useState("");
  const [filtroActivo, setFiltroActivo] = useState("");
  const paginado = usePaginado((q) => listarCheques({ ...q, buscar: filtroActivo }), [filtroActivo]);
  const cheques = paginado.items;
  const cargandoLista = paginado.cargando;
  const [comprobanteId, setComprobanteId] = useState(null);
  const [conceptosOpen, setConceptosOpen] = useState(false);
  const [nombresOpen, setNombresOpen] = useState(false);

  const cargarCatalogos = useCallback(() => {
    getCatalogosEgresos()
      .then((res) => setCatalogos(res.data))
      .catch(() => setErrorCatalogos("No se pudieron cargar los datos del formulario."));
  }, []);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);

  const aplicarBusqueda = () => {
    paginado.setPage(1);
    setFiltroActivo(buscar.trim());
  };

  if (errorCatalogos) return <Alert severity="error">{errorCatalogos}</Alert>;
  if (!catalogos) return <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
        <Tabs value={sub} onChange={(_, v) => setSub(v)} centered slotProps={{ indicator: { sx: { height: 3, bgcolor: SUB_COLORES[sub] } } }}
          sx={{ minHeight: 32, mb: 1, "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: 12.5, textTransform: "none", fontWeight: 700 } }}>
          {["Cheque", "Traspaso de dinero", "Recibo de crédito"].map((etq, i) => (
            <Tab key={etq} label={etq}
              sx={{ color: SUB_COLORES[i], opacity: sub === i ? 1 : 0.7, "&.Mui-selected": { color: SUB_COLORES[i], bgcolor: `${SUB_COLORES[i]}1f` } }} />
          ))}
        </Tabs>
        {sub === 0 && (
          <ChequeForm
            catalogos={catalogos}
            onGuardado={(id) => { setComprobanteId(id); paginado.recargar(true); }}
          />
        )}
        {sub === 1 && <TraspasoDineroForm catalogos={catalogos} />}
        {sub === 2 && <ReciboCreditoForm catalogos={catalogos} />}
      </Paper>

      {sub === 0 && (
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, bgcolor: "#0e6b6b", color: "#fff" }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.5 }}>Cheques emitidos</Typography>
          {cargandoLista && <CircularProgress size={14} sx={{ color: "#fff" }} />}
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" variant="outlined" onClick={() => setConceptosOpen(true)} sx={{ minHeight: 24, height: 24, fontSize: 11, borderRadius: 0, color: "#fff", borderColor: "rgba(255,255,255,0.7)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
            Conceptos
          </Button>
          {onIrABusqueda && (
            <Button size="small" variant="outlined" onClick={onIrABusqueda} sx={{ minHeight: 24, height: 24, fontSize: 11, borderRadius: 0, color: "#fff", borderColor: "rgba(255,255,255,0.7)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
              Búsqueda
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={() => setNombresOpen(true)} sx={{ minHeight: 24, height: 24, fontSize: 11, borderRadius: 0, color: "#fff", borderColor: "rgba(255,255,255,0.7)", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.12)" } }}>
            Nombres
          </Button>
          <TextField
            size="small" placeholder="Buscar N°, cheque, nombre, observación..." value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") aplicarBusqueda(); }}
            sx={{ width: 240, bgcolor: "background.paper", "& .MuiOutlinedInput-root": { borderRadius: 0 }, "& .MuiInputBase-input": { py: "3px", fontSize: 12 } }}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={aplicarBusqueda}><SearchIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) } }}
          />
        </Box>
        <TableContainer>
          <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx, "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" } }}>
            <TableHead>
              <TableRow>
                {["ID", "N° cheque", "Fecha", "Dirigido a", "Depositado a", "Concepto", "Servicio", "Ciudad", "Monto Bs", "Monto USD", "Banco", "Mes servicio", "N° factura", "Emitido por", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap", bgcolor: "#e3f1f1", color: "#0b4f4f", borderBottom: "2px solid #0e6b6b" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!cheques.length ? (
                <TableRow><TableCell colSpan={15}><Typography variant="body2" color="text.secondary">No existen egresos registrados.</Typography></TableCell></TableRow>
              ) : cheques.map((c) => (
                <TableRow key={c.id} hover sx={c.moneda === "Dolares" ? { bgcolor: "rgba(46, 125, 50, 0.10) !important" } : undefined}>
                  <TableCell sx={{ fontWeight: 800 }}>
                    <Tooltip title="Abrir el comprobante en otra pestaña">
                      <Link href={`/egresos/comprobante/${c.id}`} target="_blank" rel="noopener" underline="hover" sx={{ color: "#0e6b6b", fontWeight: 800 }}>{c.id}</Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{c.nocheque}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(c.fecha)}</TableCell>
                  <TableCell>{c.nombre}</TableCell>
                  <TableCell>{c.deposito}</TableCell>
                  <TableCell>{c.concepto}</TableCell>
                  <TableCell>{c.servicio}</TableCell>
                  <TableCell>{c.ciudad}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#1565c0", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(c.bolivianos)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#2e7d32", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(c.dolares)}</TableCell>
                  <TableCell>{c.banco}</TableCell>
                  <TableCell>{c.mes}</TableCell>
                  <TableCell>{(c.facturas || []).join(", ") || "—"}</TableCell>
                  <TableCell>{c.usu}</TableCell>
                  <TableCell>
                    <Tooltip title="Ver comprobante">
                      <IconButton size="small" color="primary" onClick={() => setComprobanteId(c.id)} sx={{ p: 0.3 }}>
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PiePaginacion paginado={paginado} />
      </Paper>
      )}

      <ComprobanteDialog id={comprobanteId} onClose={() => setComprobanteId(null)} onCambio={() => paginado.recargar()} />
      <ConceptosDialog open={conceptosOpen} onClose={() => setConceptosOpen(false)} onCreado={cargarCatalogos} />
      <NombresDialog open={nombresOpen} onClose={() => setNombresOpen(false)} onCreado={cargarCatalogos} />
    </Box>
  );
}
